import { TableColumn } from '../types/table';
import { toText } from '../search/utils';

/**
 * Formats a date string to dd/mm/yyyy format
 * This is the single source of truth for date formatting in the application
 *
 * @param dateString - The date string to format (ISO format or any valid date string)
 * @param fallback - The fallback value if the date is invalid (default: 'N/A')
 * @returns Formatted date string in dd/mm/yyyy format
 */
export function formatDate(dateString?: string | null, fallback: string = 'N/A'): string {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-GB');
}

/**
 * Formats a date string to dd/mm/yyyy format, using today's date as fallback
 * Useful for documents where a date is always required
 *
 * @param dateString - The date string to format (ISO format or any valid date string)
 * @returns Formatted date string in dd/mm/yyyy format (today's date if input is invalid/missing)
 */
export function formatDateOrToday(dateString?: string | null): string {
  return formatDate(dateString, new Date().toLocaleDateString('en-GB'));
}

/**
 * Extracts the cell value from a row using column configuration
 * Uses column accessor if available, otherwise direct property access
 *
 * @param row - The data row
 * @param column - The column configuration
 * @returns The extracted cell value
 */
export function getCellValue<T>(
  row: T,
  column: TableColumn<T>
): any {
  if (column.accessor) {
    return column.accessor(row);
  }
  return (row as any)[column.key];
}

/**
 * Converts a value to a sortable primitive (string or number)
 * Handles dates, numbers, and strings intelligently
 *
 * @param value - The value to convert
 * @returns A sortable primitive value
 */
export function getSortableValue(value: any): string | number {
  // Handle null/undefined
  if (value == null) return '';

  // Try number conversion
  const num = Number(value);
  if (!isNaN(num) && typeof value !== 'boolean') {
    return num;
  }

  // Try date conversion
  const date = new Date(value);
  if (!isNaN(date.getTime()) && typeof value !== 'number') {
    return date.getTime();
  }

  // Fallback to string
  return toText(value).toLowerCase();
}

/**
 * Formats a cell value for display based on column type
 * Handles special formatting for dates, numbers, and booleans
 *
 * @param value - The value to format
 * @param columnKey - The column key (used to infer type)
 * @returns Formatted display value
 */
export function formatCellValue(value: any, columnKey: string): string {
  // Handle null/undefined
  if (value == null || value === '') return '-';

  // Date columns
  if (columnKey.includes('date') || columnKey.includes('day')) {
    return formatDate(value, '-');
  }

  // Numeric columns (freight, collect, etc.)
  if (['freight', 'collect', 'price', 'amount', 'cost'].includes(columnKey)) {
    const num = Number(value);
    if (!isNaN(num)) {
      return num.toFixed(0);
    }
  }

  // Boolean columns
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return toText(value);
}

/**
 * Gets CSS classes for a table cell based on column configuration
 * Applies appropriate text alignment, whitespace, and font styles
 *
 * @param columnKey - The column key
 * @param options - Additional styling options
 * @returns CSS class string
 */
export function getCellClasses(
  columnKey: string,
  options: {
    monospace?: boolean;
    wrap?: boolean;
    align?: 'left' | 'center' | 'right';
    size?: 'xs' | 'sm' | 'base';
  } = {}
): string {
  const {
    monospace = false,
    wrap = false,
    align = 'left',
    size = 'sm'
  } = options;

  const classes: string[] = ['px-2 py-2 text-gray-900 dark:text-gray-100'];

  // Font size
  if (size === 'xs') classes.push('text-[11px]');
  else if (size === 'sm') classes.push('text-sm');
  else classes.push('text-base');

  // Font family
  if (monospace) {
    classes.push('font-mono');
  }

  // Whitespace handling
  if (wrap) {
    classes.push('whitespace-normal break-words');
  } else {
    classes.push('whitespace-nowrap');
  }

  // Text alignment
  if (align === 'center') classes.push('text-center');
  else if (align === 'right') classes.push('text-right');
  else classes.push('text-left');

  return classes.join(' ');
}

/**
 * Determines if a column should use monospace font
 * Typically used for codes, IDs, chassis numbers, etc.
 *
 * @param columnKey - The column key
 * @returns Whether to use monospace font
 */
export function isMonospaceColumn(columnKey: string): boolean {
  const monospacePatterns = ['chs_no', 'chassis', 'vin', 'code', 'id', 'ref_no', 'reference'];
  return monospacePatterns.some(pattern => columnKey.toLowerCase().includes(pattern));
}

/**
 * Determines if a column should wrap text
 * Typically used for descriptions, addresses, notes, etc.
 *
 * @param columnKey - The column key
 * @returns Whether to wrap text
 */
export function isWrapColumn(columnKey: string): boolean {
  const wrapPatterns = ['description', 'address', 'note', 'comment', 'make_model'];
  return wrapPatterns.some(pattern => columnKey.toLowerCase().includes(pattern));
}

/**
 * Gets the optimal column width based on column key and type
 * Returns width in pixels or a responsive class
 *
 * @param columnKey - The column key
 * @returns Width string (e.g., '120px', 'auto')
 */
export function getDefaultColumnWidth(columnKey: string): string {
  // Checkbox/selection columns
  if (columnKey === 'select' || columnKey === 'checkbox') return '40px';

  // ID columns
  if (columnKey === 'id') return '64px';

  // Code columns
  if (columnKey === 'code') return '96px';

  // Date/time columns
  if (columnKey.includes('date') || columnKey.includes('time') || columnKey === 'day') {
    return '128px';
  }

  // Measurement columns
  if (['weight', 'length', 'width', 'height', 'cbm'].some(k => columnKey.includes(k))) {
    return '80px';
  }

  // Loaded/boolean columns
  if (columnKey === 'loaded' || columnKey.includes('is_')) return '80px';

  // Long text columns
  if (['description', 'address', 'comment', 'note'].some(k => columnKey.includes(k))) {
    return '192px';
  }

  // Chassis/VIN numbers
  if (['chs_no', 'chassis', 'vin'].some(k => columnKey.includes(k))) {
    return '170px';
  }

  // Default
  return '96px';
}
